import { createPayloadAction, createRequestActionCreators, RequestAction } from 'helpers/actionsHelpers'
import {
  LinkageForm,
  LinkageFormCollection,
  LinkageSession,
  LinkageSessionId,
  LinkedMixedAccount,
  LinkedMixedOperation,
  LiveUpdateInfo,
  ManualBalanceAccount,
  ManualBalanceAccountCollection,
  MixedCompositeId,
  ProcessedRawProviderAccount,
  Provider,
  ProviderAccount,
  ProviderAccountCollection,
  ProviderAccountStatesCollection,
  ProviderBalanceAccount,
  ProviderBalanceAccountCollection,
  ProviderCollection,
  ProviderId,
  WithLinkageSessionId,
} from 'interfaces/linkage'
import { PayloadAction } from 'interfaces/action'
import { createRequestAction } from 'helpers/actionHelpers/createRequestAction'

export const GET_PROVIDERS = 'GET_PROVIDERS' as const
export type GetProvidersPayload =
  | { mode: 'popular' }
  | { mode: 'search', query: string }
type GetProviders = RequestAction<typeof GET_PROVIDERS, GetProvidersPayload, ProviderCollection>
export const getProvidersActionCreator = createRequestActionCreators<typeof GET_PROVIDERS, GetProvidersPayload, ProviderCollection>(GET_PROVIDERS)

export const SEARCH_PROVIDERS = 'SEARCH_PROVIDERS' as const
export type SearchProvidersPayload = {
  query: string
}
type SearchProviders = RequestAction<typeof SEARCH_PROVIDERS, SearchProvidersPayload, ProviderCollection>
export const searchProvidersActionCreator = createRequestActionCreators<typeof SEARCH_PROVIDERS, SearchProvidersPayload, ProviderCollection>(SEARCH_PROVIDERS)

export const CLEAR_PROVIDERS_SEARCH = 'CLEAR_PROVIDERS_SEARCH' as const
type ClearProvidersSearchPayload = {}
export const clearProvidersSearchActionCreator = (payload: ClearProvidersSearchPayload): PayloadAction<typeof CLEAR_PROVIDERS_SEARCH, ClearProvidersSearchPayload> =>
  createPayloadAction(CLEAR_PROVIDERS_SEARCH, { payload })
export type ClearProvidersSearch = ReturnType<typeof clearProvidersSearchActionCreator>


type GetDetailedProviderRequestPayload = {
  session: LinkageSession
  providerId: ProviderId
}
type GetDetailedProviderSuccessPayload = {
  provider: Provider
  form: LinkageForm
}
export const GET_DETAILED_PROVIDER = 'GET_DETAILED_PROVIDER' as const
type GetDetailedProvider = RequestAction<typeof GET_DETAILED_PROVIDER, GetDetailedProviderRequestPayload, GetDetailedProviderSuccessPayload>
export const getDetailedProviderActionCreator = createRequestActionCreators<
  typeof GET_DETAILED_PROVIDER, GetDetailedProviderRequestPayload, GetDetailedProviderSuccessPayload
>(GET_DETAILED_PROVIDER)


export const GET_MIXED_ACCOUNTS = 'GET_MIXED_ACCOUNTS' as const
export type GetMixedAccountsSuccessPayload = {
  providers: ProviderCollection
  providerAccounts: ProviderAccountCollection
  providerAccountStates: ProviderAccountStatesCollection
  providerBalanceAccounts: ProviderBalanceAccountCollection
  manualBalanceAccounts: ManualBalanceAccountCollection
  forms: LinkageFormCollection
}
export type GetMixedAccounts = RequestAction<typeof GET_MIXED_ACCOUNTS, {}, GetMixedAccountsSuccessPayload>
export const getMixedAccountsActionCreator = createRequestActionCreators<
  typeof GET_MIXED_ACCOUNTS, {}, GetMixedAccountsSuccessPayload
>(GET_MIXED_ACCOUNTS)


export type AuthenticateLinkageRequestPayload = {
  session: LinkageSession
  formValues: {}
}
type AuthenticateLinkageSuccessPayload = ProcessedRawProviderAccount
export const AUTHENTICATE_LINKAGE = 'AUTHENTICATE_LINKAGE' as const
type AuthenticateLinkage = RequestAction<typeof AUTHENTICATE_LINKAGE, AuthenticateLinkageRequestPayload, AuthenticateLinkageSuccessPayload>
export const authenticateLinkageActionCreator = createRequestActionCreators<
  typeof AUTHENTICATE_LINKAGE, AuthenticateLinkageRequestPayload, AuthenticateLinkageSuccessPayload
>(AUTHENTICATE_LINKAGE)

export type PollProviderAccountRequestPayload = ProviderAccount
export type PollProviderAccountSuccessPayload = ProcessedRawProviderAccount
export const POLL_PROVIDER_ACCOUNT = 'POLL_PROVIDER_ACCOUNT' as const
type PollProviderAccount = RequestAction<typeof POLL_PROVIDER_ACCOUNT, PollProviderAccountRequestPayload, PollProviderAccountSuccessPayload>
export const pollProviderAccountActionCreator = createRequestActionCreators<
  typeof POLL_PROVIDER_ACCOUNT, PollProviderAccountRequestPayload, PollProviderAccountSuccessPayload
>(POLL_PROVIDER_ACCOUNT)

export type PerformMixedAccountOperationRequestPayload = {
  account: LinkedMixedAccount | ProviderBalanceAccount
  operation: LinkedMixedOperation
  // no request to the server
  noop?: boolean
}
export const PERFORM_MIXED_ACCOUNT_OPERATION = 'PERFORM_MIXED_ACCOUNT_OPERATION' as const
export type PerformMixedAccountOperation = RequestAction<typeof PERFORM_MIXED_ACCOUNT_OPERATION, PerformMixedAccountOperationRequestPayload, {}>
export const performMixedAccountOperationActionCreator = createRequestActionCreators<
  typeof PERFORM_MIXED_ACCOUNT_OPERATION, PerformMixedAccountOperationRequestPayload, {}
>(PERFORM_MIXED_ACCOUNT_OPERATION)


type CancelMfaRequestPayload = ProviderAccount
type CancelMfaSuccessPayload = ProcessedRawProviderAccount
export const CANCEL_MFA = 'CANCEL_MFA' as const
type CancelMfa = RequestAction<typeof CANCEL_MFA, CancelMfaRequestPayload, CancelMfaSuccessPayload>
export const cancelMfaActionCreator = createRequestActionCreators<
  typeof CANCEL_MFA, CancelMfaRequestPayload, CancelMfaSuccessPayload
>(CANCEL_MFA)

// TODO: I had to implement this action because manual account creation/editing is being performed via formEpic
// and format of `formSubmittedSuccessfully` action is insufficient
export const SET_MANUAL_BALANCE_ACCOUNT = 'SET_MANUAL_BALANCE_ACCOUNT' as const
type SetManualBalanceAccount = PayloadAction<typeof SET_MANUAL_BALANCE_ACCOUNT, ManualBalanceAccount>
export const setManualBalanceAccountActionCreator = (manualBalanceAccount: ManualBalanceAccount): SetManualBalanceAccount =>
  createPayloadAction(SET_MANUAL_BALANCE_ACCOUNT, manualBalanceAccount)


/*
 LINKAGE SESSION
 */

type StartLinkageSessionRequestPayload = {
  cid: MixedCompositeId
}
type StartLinkageSessionSuccessPayload = WithLinkageSessionId & {
  loading?: boolean
}
export const START_LINKAGE_SESSION = 'START_LINKAGE_SESSION' as const
type StartLinkageSession = RequestAction<typeof START_LINKAGE_SESSION, StartLinkageSessionRequestPayload, StartLinkageSessionSuccessPayload>
export const startLinkageSessionActionCreator = createRequestActionCreators<
  typeof START_LINKAGE_SESSION, StartLinkageSessionRequestPayload, StartLinkageSessionSuccessPayload
>(START_LINKAGE_SESSION)

export const KILL_LINKAGE_SESSION = 'KILL_LINKAGE_SESSION' as const
type KillLinkageSession = PayloadAction<typeof KILL_LINKAGE_SESSION, WithLinkageSessionId>
export const killLinkageSessionActionCreator = (sessionId: LinkageSessionId): KillLinkageSession =>
  createPayloadAction(KILL_LINKAGE_SESSION, { sessionId })

export const FETCH_MISSING_ACCOUNTS = 'FETCH_MISSING_ACCOUNTS' as const
export class FetchMissingAccountsAction extends createRequestAction<typeof FETCH_MISSING_ACCOUNTS, {}, {}>(FETCH_MISSING_ACCOUNTS) { }

export const REQUEST_LINKAGE_SESSION = 'REQUEST_LINKAGE_SESSION' as const
type RequestLinkageSession = PayloadAction<typeof REQUEST_LINKAGE_SESSION, { cid: MixedCompositeId }>
export const requestLinkageSessionActionCreator = (cid: MixedCompositeId): RequestLinkageSession =>
  createPayloadAction(REQUEST_LINKAGE_SESSION, { cid })

/*
 UI Actions
 */

type ConfirmMixedAccountOperationPayload = {
  mixedAccount: LinkedMixedAccount | ProviderBalanceAccount
  operation: LinkedMixedOperation | null
}
export const CONFIRM_MIXED_ACCOUNT_OPERATION = 'CONFIRM_MIXED_ACCOUNT_OPERATION' as const
type ConfirmMixedAccountOperation = PayloadAction<typeof CONFIRM_MIXED_ACCOUNT_OPERATION, ConfirmMixedAccountOperationPayload>
export const confirmMixedAccountOperationActionCreator = (mixedAccount: LinkedMixedAccount | ProviderBalanceAccount, operation: LinkedMixedOperation | null): ConfirmMixedAccountOperation =>
  createPayloadAction(CONFIRM_MIXED_ACCOUNT_OPERATION, { mixedAccount, operation })

export const CREATE_FAKE_PROVIDER_ACCOUNT = 'CREATE_FAKE_PROVIDER_ACCOUNT' as const
type CreateFakeProviderAccount = PayloadAction<typeof CREATE_FAKE_PROVIDER_ACCOUNT, ProviderAccount>
export const createFakeProviderAccountActionCreator = (providerAccount: ProviderAccount): CreateFakeProviderAccount =>
  createPayloadAction(CREATE_FAKE_PROVIDER_ACCOUNT, providerAccount)

type SetLinkageProgressPayload = {
  cid: MixedCompositeId
  value: number | null
}
export const SET_LINKAGE_PROGRESS = 'SET_LINKAGE_PROGRESS' as const
type SetLinkageProgress = PayloadAction<typeof SET_LINKAGE_PROGRESS, SetLinkageProgressPayload>
export const setLinkageProgressActionCreator = (cid: MixedCompositeId, value: number | null): SetLinkageProgress =>
  createPayloadAction(SET_LINKAGE_PROGRESS, { cid, value })

type ClearRecentlyAddedRequestPayload = {}
type ClearRecentlyAddedSuccessPayload = {
  providerAccountCids: MixedCompositeId[]
}
export const CLEAR_RECENTLY_ADDED = 'CLEAR_RECENTLY_ADDED' as const
type ClearRecentlyAdded = RequestAction<typeof CLEAR_RECENTLY_ADDED, ClearRecentlyAddedRequestPayload, ClearRecentlyAddedSuccessPayload>
export const clearRecentlyAddedActionCreator = createRequestActionCreators<
  typeof CLEAR_RECENTLY_ADDED, ClearRecentlyAddedRequestPayload, ClearRecentlyAddedSuccessPayload
>(CLEAR_RECENTLY_ADDED)


/*
 Live Update Actions
 */
type PollLiveUpdateRequestPayload = {
  info: LiveUpdateInfo | null
  isRepeatedRequest?: boolean
}
type PollLiveUpdateSuccessPayload = LiveUpdateInfo
export const POLL_LIVE_UPDATE = 'POLL_LIVE_UPDATE' as const
type PollLiveUpdate = RequestAction<typeof POLL_LIVE_UPDATE, PollLiveUpdateRequestPayload, PollLiveUpdateSuccessPayload>
export const pollLiveUpdateActionCreator = createRequestActionCreators<
  typeof POLL_LIVE_UPDATE, PollLiveUpdateRequestPayload, PollLiveUpdateSuccessPayload
>(POLL_LIVE_UPDATE)

type FlushLiveUpdateSuccessPayload = LiveUpdateInfo
export const FLUSH_LIVE_UPDATE = 'FLUSH_LIVE_UPDATE' as const
type FlushLiveUpdate = RequestAction<typeof FLUSH_LIVE_UPDATE, {}, FlushLiveUpdateSuccessPayload>
export const flushLiveUpdateActionCreator = createRequestActionCreators<
  typeof FLUSH_LIVE_UPDATE, {}, FlushLiveUpdateSuccessPayload
>(FLUSH_LIVE_UPDATE)

type LinkageResultEntity = 'first_account' | 'credit_profile'
export type LinkageResultPayload = {
  'new': LinkageResultEntity
  all: LinkageResultEntity[]
}
export const LINKAGE_RESULT = 'LINKAGE_RESULT' as const
export type LinkageResult = PayloadAction<typeof LINKAGE_RESULT, LinkageResultPayload>
export const linkageResultActionCreator = (data: LinkageResultPayload): LinkageResult =>
  createPayloadAction(LINKAGE_RESULT, data)


type Action =
  | GetProviders
  | SearchProviders
  | ClearProvidersSearch
  | GetDetailedProvider
  | GetMixedAccounts
  | AuthenticateLinkage
  | PollProviderAccount
  | CreateFakeProviderAccount
  | SetLinkageProgress
  | PerformMixedAccountOperation
  | SetManualBalanceAccount
  | CancelMfa
  | StartLinkageSession
  | FetchMissingAccountsAction
  | KillLinkageSession
  | RequestLinkageSession
  | ConfirmMixedAccountOperation
  | PollLiveUpdate
  | FlushLiveUpdate
  | LinkageResult
  | ClearRecentlyAdded

export default Action
